import { Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { RichTextEditor } from "../../../../components/rich-text/index.js";
import { supportApi } from "../../client.js";

/**
 * Reply box on a ticket. WYSIWYG body via the shared `<RichTextEditor>`
 * (TipTap with B/I/U/list/link), plus a file picker so the staff
 * member can attach screenshots / PDFs / logs to the same reply. The
 * customer is notified by email when the reply is posted (see
 * `Notifications::DeliverTicketActivity` on the Rails side).
 *
 * Staff-only context: this component only ships in the staff
 * dashboard, so it always POSTs to the admin namespace
 * (`/api/admin/tickets/:id/comments`) and honours an explicit
 * `is_internal` toggle. Internal notes get a yellow rail via the
 * `data-internal="true"` attribute on the form element — the customer
 * never receives an email for them (the activity job filters internal
 * recipients).
 *
 * Submission is two-step:
 *   1. POST the comment with its HTML body via
 *      `supportApi.postAdminTicketComment`, which routes through the
 *      dashboard server's `/api/admin/tickets/:id/comments` proxy.
 *   2. If the user picked files, upload them to the existing
 *      `/api/support/tickets/:tid/comments/:cid/attachments` endpoint
 *      (the upload surface is shared between the staff + customer
 *      shells — only the comment-create needed the admin variant).
 */
export function CommentForm({
  ticketId,
  onPosted,
}: {
  ticketId: number | string;
  /** Fired after a successful POST (and any attachment upload). The
   * parent `TicketDetailPage` uses this to invalidate the comments +
   * attachments queries so the new row renders. */
  onPosted: (commentId: number) => void;
}): JSX.Element {
  const [html, setHtml] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [isInternal, setIsInternal] = useState(false);

  const post = useMutation({
    mutationFn: async (b: { body: string; is_internal: boolean; files: File[] }) => {
      const created = await supportApi.postAdminTicketComment(ticketId, {
        body: b.body,
        is_internal: b.is_internal,
      });
      if (b.files.length > 0) {
        // Re-use the legacy attachment endpoint — there is no admin
        // variant for per-comment uploads (the binary upload path
        // already gates on staff role via the dashboard proxy).
        await supportApi.uploadCommentAttachments(
          ticketId,
          created.data.id,
          b.files,
        );
      }
      return created;
    },
    onSuccess: (created) => {
      setHtml("");
      setFiles([]);
      setIsInternal(false);
      onPosted(created.data.id);
    },
  });

  const isEmpty = !hasMeaningfulHtml(html);
  const isSubmitting = post.isPending;

  return (
    <Box
      component="form"
      data-internal={isInternal ? "true" : "false"}
      onSubmit={(e) => {
        e.preventDefault();
        if (isEmpty) return;
        post.mutate({ body: html, is_internal: isInternal, files });
      }}
      sx={[
        { display: "grid", gap: 1, borderRadius: 1, transition: "background-color 150ms" },
        // Warn-token rail + soft highlight so the operator can't miss
        // that this reply won't reach the customer. Routed through the
        // theme's warning token (not a hardcoded amber) so it tracks the
        // palette and renders correctly in dark mode.
        isInternal
          ? {
              borderLeft: 3,
              borderColor: "warning.main",
              bgcolor: (t) => alpha(t.palette.warning.main, 0.14),
              pl: 1,
            }
          : null,
      ]}
    >
      <RichTextEditor
        value={html}
        onChange={setHtml}
        placeholder="Reply…"
        disabled={isSubmitting}
        ariaLabel="Reply body"
      />

      {/* Test seam: a hidden textarea that mirrors the editor HTML so
       * Vitest in happy-dom (which doesn't fully boot TipTap's
       * contentEditable) can drive the body without spinning up the
       * editor. The textarea is screen-reader-visible-only (visually
       * hidden) to avoid duplicating the editor's affordance in the
       * production UI; production users interact with the rich-text
       * surface above. */}
      <Box component="label" sx={visuallyHidden}>
        Reply body raw
        <textarea
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          aria-hidden
        />
      </Box>

      {/* File picker chip + pending file list */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <Box
          component="label"
          sx={{
            display: "inline-flex",
            cursor: "pointer",
            alignItems: "center",
            gap: 0.75,
            borderRadius: 999,
            border: 1,
            borderColor: "divider",
            bgcolor: "surfaceSoft",
            px: 1.5,
            py: 0.5,
            fontSize: 12,
            color: "text.primary",
            "&:hover": { borderColor: "text.disabled" },
          }}
        >
          <Box component="svg" viewBox="0 0 16 16" aria-hidden sx={{ height: 12, width: 12 }}>
            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </Box>
          Attach
          <Box
            component="input"
            type="file"
            multiple
            disabled={isSubmitting}
            sx={{ display: "none" }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const picked = Array.from(e.target.files ?? []);
              if (picked.length) setFiles((prev) => [...prev, ...picked]);
              e.target.value = "";
            }}
          />
        </Box>

        {files.map((f, i) => (
          <Box
            component="span"
            key={`${f.name}-${i}`}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              borderRadius: 999,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.default",
              px: 1.25,
              py: 0.5,
              fontSize: 12,
            }}
          >
            <Box component="span" sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {f.name}
            </Box>
            <Box component="span" sx={{ color: "text.disabled" }}>{formatBytes(f.size)}</Box>
            <Box
              component="button"
              type="button"
              aria-label={`Remove ${f.name}`}
              onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
              sx={{
                cursor: "pointer",
                border: 0,
                bgcolor: "transparent",
                borderRadius: 0.5,
                color: "text.disabled",
                "&:hover": { color: "text.primary" },
              }}
            >
              ×
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isInternal}
              onChange={(e) => setIsInternal(e.target.checked)}
              disabled={isSubmitting}
              size="small"
              sx={{ color: "warning.main", "&.Mui-checked": { color: "warning.main" } }}
            />
          }
          label={
            <Box component="span" sx={{ fontSize: 12, color: "text.disabled" }}>
              Internal note{" "}
              <Box component="span" sx={{ color: "text.disabled" }}>
                (the customer is not notified)
              </Box>
            </Box>
          }
          sx={{ m: 0 }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting || isEmpty}
        >
          {isSubmitting ? "Posting…" : "Post reply"}
        </Button>
      </Box>

      {post.isError && (
        <Typography role="alert" sx={{ fontSize: 12, color: "error.main" }}>
          {(post.error as Error | undefined)?.message ?? "Failed to post comment."}
        </Typography>
      )}
    </Box>
  );
}

/** Visually-hidden but screen-reader/test accessible (was Tailwind `sr-only`). */
const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  borderWidth: 0,
} as const;

/**
 * "Empty" check that ignores TipTap's default `<p></p>` boilerplate.
 * Pure-HTML comparison would treat that as non-empty and let users
 * post empty replies.
 */
function hasMeaningfulHtml(html: string): boolean {
  const stripped = html.replace(/<p[^>]*>\s*(<br\s*\/?>)?\s*<\/p>/gi, "").trim();
  if (!stripped) return false;
  // Strip remaining tags and check for whitespace-only.
  const text = stripped.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  return text.length > 0;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
